/* global document, window, localStorage, sessionStorage */
import App from 'app/components/App';
import {
  ProvideHeadState,
  ProvideHeadDomComitter,
} from 'app/utils/HeadContext';
import {
  HeadState,
  getHeadElements,
} from 'app/utils/HeadContext/server';
import { HttpStatusState, ProvideHttpStatusState } from 'app/utils/HttpStatusState';
import FallbackErrorBoundary, { FallbackMessage } from 'app/components/FallbackErrorBoundary';
import {
  createAutoLoaderCache,
  clearCache,
  waitForPending,
  CacheProvider,
  throwOnError,
  makeCachedLoader,
} from 'app/containers/state/makeCachedLoader';
import { LoadingStage } from 'app/containers/state/LoadingState';
import injectNormalizeCss from 'app/globalStyles/normalize';
import { AUTH_PREFIX } from 'app/models/auth/constants';
import { init } from 'app/models/env/actions';
import { CLIENT_PREFIX } from 'app/remote/client/constants';
import { reset as resetAudio } from 'app/services/audio';
import FluentdLogger from 'app/services/fluentdLogger';
import { createHistory, setGlobalHistory } from 'app/services/history';
import { reset as resetHoneybadger, setBeforeSendCallback } from 'app/services/honeybadger';
import { getErrorReportingContext } from 'app/utils/errorReportingContext';
import createStore from 'app/store';
import { createStorageStore, StorageType, resetStorage } from 'app/utils/StorageContext';
import { isServerRendering } from 'app/utils/env';
import { subscribeRequestMonitor } from 'app/utils/request';
import RequestMonitor from 'app/utils/RequestMonitor';
import React from 'react';
import ReactDOM from 'react-dom';
import { reset as resetStyles } from 'app/utils/css';
import generateIndexFile from 'app/static/render/Index';
import AppBootstrapErrorBoundary from 'app/components/AppBootstrapErrorBoundary';
import { ProvideSelectedRoute, SelectedRoute } from 'app/components/utilities/SelectedRoute';
import AnalyticsServiceProvider from 'app/components/analytics/AnalyticsServiceProvider';

const APP_CONTAINER_SELECTOR = '#app-container';

const initErrorReporting = (store) => {
  setBeforeSendCallback((error, context) => {
    try {
      const state = store.getState();
      const errorReportingContext = getErrorReportingContext();

      /* eslint-disable no-param-reassign */
      context.authState = state.get(AUTH_PREFIX);
      context.clientState = state.get(CLIENT_PREFIX);

      for (const [key, value] of Object.entries(errorReportingContext)) {
        context[key] = value;
      }
      /* eslint-enable no-param-reassign */
    } catch (getStateError) {
      // It's possible for getState() to throw an error if notify() has been called from a reducer.
      const placeholder = `Error getting store state: ${getStateError}`;

      /* eslint-disable no-param-reassign */
      context.authState = placeholder;
      context.clientState = placeholder;
      /* eslint-enable no-param-reassign */
    }
  });
};

if (isServerRendering()) {
  const MIN_IDLE_MS = 100;

  // Put server-rendered content inside a <noscript> so it is hidden to users with JavaScript
  // enabled, but indexed by search engines. Later, we may want to pre-render the content and show
  // it to users for performance, but that should probably handle rehydrating of 1. store state,
  // 2. Dynamically injected CSS, and 3. lazily loaded components.
  //
  // NOTE: The <noscript> tag must be added outside of React, because React does not render
  // components inside of <noscript> tags.
  const containerElement = document.querySelector(APP_CONTAINER_SELECTOR);

  const renderApp = (appElement) => new Promise((resolve, reject) => {
    ReactDOM.render(
      appElement,
      containerElement,
      resolve,
    );
  });

  const resetGlobalState = () => {
    // Note: resetStyles should be removed when we switch to using emotion's SSR API
    resetStyles();

    // JSDOM doesn't currently implement localStorage/sessionStorage, but putting this here just
    // in case they exist in future.
    typeof localStorage !== 'undefined' && localStorage.clear();
    typeof sessionStorage !== 'undefined' && sessionStorage.clear();

    // Reset stateful global services.
    resetAudio();
    resetHoneybadger();
    FluentdLogger.clearContext();
  };

  // When server rendering, instead of immediately rendering the app, just expose a function that
  // can be used to render the app on-demand.
  window.main = async (requestedUrl, cancelToken) => {
    resetGlobalState();

    const headState = new HeadState();

    const requestMonitor = new RequestMonitor();
    const unsubscribeRequestMonitor = subscribeRequestMonitor(requestMonitor);
    const historyService = createHistory({
      useBrowserHistory: false,
      initialUrl: requestedUrl,
    });
    const initialUrl = historyService.location.href;
    const didRedirect = () => initialUrl !== historyService.location.href;
    const getRedirectResponse = () => {
      const redirectUrl = historyService.location.href;

      return {
        redirectUrl,
        status: 302,
        content: `Redirecting to ${redirectUrl}...`,
      };
    };

    const httpStatusState = new HttpStatusState(200);
    const selectedRoute = new SelectedRoute();

    setGlobalHistory(historyService);

    const autoLoaderCache = createAutoLoaderCache();

    const localStorageStore = createStorageStore(StorageType.LOCAL);
    const sessionStorageStore = createStorageStore(StorageType.SESSION);

    try {
      const store = createStore();

      initErrorReporting(store);

      injectNormalizeCss();

      const services = await store.dispatch(init({
        disableClientChecking: true,
        historyService,
      }));

      const appProps = {
        store,
        localStorageStore,
        sessionStorageStore,
        ...services,
      };

      await renderApp(
        <FallbackErrorBoundary>
          <ProvideHeadState value={headState}>
            <ProvideHttpStatusState value={httpStatusState}>
              <ProvideSelectedRoute value={selectedRoute}>
                <CacheProvider store={autoLoaderCache}>
                  <App {...appProps} />
                </CacheProvider>
              </ProvideSelectedRoute>
            </ProvideHttpStatusState>
          </ProvideHeadState>
        </FallbackErrorBoundary>,
      );

      let unlistenRedirect;
      await Promise.race([
        // If we redirect, immediately return a redirect response and don't wait
        // for the page to finish rendering.
        new Promise(resolve => {
          unlistenRedirect = historyService.listen(() => {
            if (didRedirect()) {
              resolve();
            }
          });
        }),

        // Wait until all components and data have finished loading.
        (async () => {
          // Old approach
          await requestMonitor.onRequestIdle(MIN_IDLE_MS, cancelToken);

          // New approach. When enough code is using makeCachedLoader, the old
          // approach can be removed. For now, though, we have to use both.
          await waitForPending(autoLoaderCache, cancelToken);
        })(),
      ]);

      if (unlistenRedirect) {
        unlistenRedirect();
      }

      if (didRedirect()) {
        return getRedirectResponse();
      }

      // When we switch to using ReactDOMServer for rendering (instead of using
      // ReactDOM + JSDOM like we do now), we can use the following code to
      // render the app and wait until requests are done:
      //
      // ```
      // let html: string = '';
      //
      // const maxIterations = 10;
      // for (let i = 0; i < maxIterations; i++) {
      //   const serverRenderLoader = new ServerRenderLoader(autoLoaderCache);
      //
      //   html = ReactDOMServer.renderToStaticMarkup(
      //     <FallbackErrorBoundary>
      //       <ServerRenderLoaderProvider value={serverRenderLoader}>
      //         <LoadDeps />
      //       </ServerRenderLoaderProvider>
      //     </FallbackErrorBoundary>,
      //   );
      //
      //   if (!hasPending(autoLoaderCache)) {
      //     break;
      //   } else {
      //     await waitForPending(autoLoaderCache);
      //   }
      // }
      // ```

      // Extract meta tags
      const metaElements = getHeadElements(headState);

      // Extract app content
      //
      // Note: In the future this should use `renderToStaticMarkup()` instead of
      // relying on JSDOM.
      const html = containerElement.innerHTML;

      // Extract CSS
      //
      // Unfortunately, we cannot use Emotion's SSR features to extract the
      // critical CSS, because those features are completely disabled if you are
      // running in a browser enviroment like JSDOM. If we switch to running the
      // SSR server directly in node/ts-node, then we should switch to using
      // `renderStylesToString()` here.
      const injectedStyleTags = [...document.querySelectorAll('style[data-emotion]')];
      // Note: Using innerHTML to serialize the <style> tags works because we
      // force emotion to not use insertRule() when server rendering.
      const css = injectedStyleTags.map(tag => tag.innerHTML).join('');
      const styleElements = [<style key="emotion" data-emotion dangerouslySetInnerHTML={{ __html: css }} />];

      // window.serverRenderInfo is set by serverRender script
      const { bundleScripts, version } = window.serverRenderInfo;

      const { content } = generateIndexFile({
        bundleScripts,
        version,
        lang: services.localizationService.getLocale(),
        serverRenderedContent: {
          html,
          styleElements,
          metaElements,
        },
      });

      return {
        content,
        status: httpStatusState.status,
        selectedRoute: selectedRoute.id,
      };
    } catch (error) {
      await renderApp(
        <FallbackMessage error={error} />,
      );

      // <FallbackMessage> is a very simple component and should render
      // synchronously, so we can access DOM immediately.
      const html = containerElement.innerHTML;

      const { bundleScripts, version } = window.serverRenderInfo;

      const { content } = generateIndexFile({
        bundleScripts,
        version,
        serverRenderedContent: {
          html,
          styleElements: [],
          metaElements: [],
        },
      });

      return {
        content,
        status: 500,
      };
    } finally {
      // Do cleanup in finally block, so it still happens even if request is
      // cancelled via cancelToken.
      ReactDOM.unmountComponentAtNode(containerElement);

      // Clear cache so that any pending loads get cancelled.
      clearCache(autoLoaderCache);

      resetStorage(localStorageStore);
      resetStorage(sessionStorageStore);

      unsubscribeRequestMonitor();
    }
  };
} else {
  const autoLoaderCache = createAutoLoaderCache();

  const { useLoader } = makeCachedLoader(async () => {
    const historyService = createHistory({ useBrowserHistory: true });
    setGlobalHistory(historyService);
    const store = createStore();

    initErrorReporting(store);

    injectNormalizeCss();

    const services = await store.dispatch(init({ historyService }));

    const localStorageStore = createStorageStore(StorageType.LOCAL);
    const sessionStorageStore = createStorageStore(StorageType.SESSION);

    return {
      store,
      localStorageStore,
      sessionStorageStore,
      ...services,
    };
  });

  // Initialize app services inside of a React component so that we can trigger
  // the error boundary and show an error message to the user if something
  // throws an error.
  const LoadDeps = ({ AppComponent }) => {
    const { stage, value } = throwOnError(useLoader({ args: null }));

    if (stage === LoadingStage.Loading) {
      return null;
    } else {
      return <AppComponent {...value} />;
    }
  };

  const renderApp = (AppComponent) => {
    ReactDOM.render(
      <FallbackErrorBoundary>
        <AppBootstrapErrorBoundary>
          <ProvideHeadDomComitter>
            <CacheProvider store={autoLoaderCache}>
              <AnalyticsServiceProvider>
                <LoadDeps AppComponent={AppComponent} />
              </AnalyticsServiceProvider>
            </CacheProvider>
          </ProvideHeadDomComitter>
        </AppBootstrapErrorBoundary>
      </FallbackErrorBoundary>,
      document.querySelector(APP_CONTAINER_SELECTOR),
    );
  };

  if (module.hot) {
    module.hot.accept('app/components/App', () => {
      const NewApp = require('app/components/App').default; // eslint-disable-line global-require
      renderApp(NewApp);
    });
  }

  renderApp(App);
}
